import React, { useState, useMemo, useEffect } from 'react';
import { MyWebRequest, CoupleSettings, MemoryItem, DailyLetter } from '../../types';
import { AdminPage, AdminSection } from '../AdminPage';
import { UploadModal } from '../UploadModal';

export type UserAdminSection =
  | 'dashboard'
  | 'couple'
  | 'memories'
  | 'letters'
  | 'music'
  | 'phrases'
  | 'tulips'
  | 'watering'
  | 'atmosphere';

interface UserWebAdminPanelProps {
  request: MyWebRequest;
  onUpdateWeb: (updatedRequest: MyWebRequest) => Promise<void> | void;
  onClose: () => void;
  onLogoutPin: () => void;
}

export const UserWebAdminPanel: React.FC<UserWebAdminPanelProps> = ({
  request,
  onUpdateWeb,
  onClose,
  onLogoutPin,
}) => {
  const [activeProfile, setActiveProfile] = useState<'novio' | 'novia'>('novio');
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState<AdminSection>('dashboard');

  // Memories & Letters local state synced with request - starts 100% clean with 0 items
  const [memories, setMemories] = useState<MemoryItem[]>(() => request.customMemories || []);
  const [lettersList, setLettersList] = useState<DailyLetter[]>(() => request.customLetters || []);

  useEffect(() => {
    if (request.customMemories) setMemories(request.customMemories);
  }, [request.customMemories]);

  useEffect(() => {
    if (request.customLetters) setLettersList(request.customLetters);
  }, [request.customLetters]);

  // Construct standard CoupleSettings for this custom web - clean slate with no residual info from original web
  const effectiveSettings: CoupleSettings = useMemo(() => {
    return {
      partner1Name: request.customSettings?.partner1Name || request.applicantName || 'Él',
      partner2Name: request.customSettings?.partner2Name || request.partnerName || 'Ella',
      partner1Avatar: request.customSettings?.partner1Avatar || '',
      partner2Avatar: request.customSettings?.partner2Avatar || '',
      partner1Title: request.customSettings?.partner1Title || 'El Novio',
      partner2Title: request.customSettings?.partner2Title || 'La Novia',
      partner1BirthDate: request.customSettings?.partner1BirthDate || '',
      partner2BirthDate: request.customSettings?.partner2BirthDate || '',
      partner1BloodType: request.customSettings?.partner1BloodType || 'O+',
      partner2BloodType: request.customSettings?.partner2BloodType || 'O+',
      startDate: request.customSettings?.startDate || request.anniversaryDate || '',
      quote: request.customSettings?.quote || request.dedication || '',
      location: request.customSettings?.location || '',
      partner1Email: request.partner1Email || request.customSettings?.partner1Email || request.email || '',
      partner2Email: request.partner2Email || request.customSettings?.partner2Email || '',
      tulipSettings: request.customSettings?.tulipSettings || {
        mode: 'all',
        allColor: request.customTulips?.defaultColor || 'golden',
        individualColors: ['golden', 'golden', 'golden', 'golden'],
      },
      gardenWatering: request.customSettings?.gardenWatering || {
        lastWateredAt: request.customWatering?.lastWateredAt || new Date().toISOString(),
        wateringIntervalDays: request.customWatering?.wateringIntervalDays || 3,
        wateredCount: request.customWatering?.wateredCount || 0,
        witheringEnabled: true,
      },
      loveNotes: request.customSettings?.loveNotes || [],
      bannerPhrases: request.customSettings?.bannerPhrases || [],
      playlist: request.customSettings?.playlist || (request.specialSong ? [{
        id: `song-${request.id}`,
        title: request.specialSong,
        artist: request.applicantName || 'Nosotros',
        url: request.specialSong,
        isSpecialTrack: true
      }] : []),
      theme: request.customSettings?.theme || 'rose',
      atmosphereMode: request.customSettings?.atmosphereMode || 'auto',
      manualTimeOfDay: request.customSettings?.manualTimeOfDay || 'day',
      manualWeather: request.customSettings?.manualWeather || 'clear',
      manualMinuteOfDay: request.customSettings?.manualMinuteOfDay ?? 720,
      navVisibility: {
        garden: request.customSettings?.navVisibility?.garden ?? true,
        blog: request.customSettings?.navVisibility?.blog ?? true,
        letters: request.customSettings?.navVisibility?.letters ?? true,
        animations: request.customSettings?.navVisibility?.animations ?? true,
        myWeb: false, // Never show myWeb in custom web
      },
      musicEnabled: request.customSettings?.musicEnabled ?? true,
      musicAutoplay: request.customSettings?.musicAutoplay ?? false,
      musicVolume: request.customSettings?.musicVolume ?? 0.7,
      musicLoopMode: request.customSettings?.musicLoopMode || 'all',
      activeSongId: request.customSettings?.activeSongId,
    };
  }, [request]);

  // Master update handler: saves CoupleSettings back into MyWebRequest
  const handleSaveSettings = async (newSettings: CoupleSettings) => {
    const updated: MyWebRequest = {
      ...request,
      applicantName: newSettings.partner1Name,
      partnerName: newSettings.partner2Name,
      anniversaryDate: newSettings.startDate,
      dedication: newSettings.quote,
      partner1Email: newSettings.partner1Email || request.partner1Email,
      partner2Email: newSettings.partner2Email || request.partner2Email,
      customSettings: {
        ...(request.customSettings || {}),
        ...newSettings,
        navVisibility: {
          ...(newSettings.navVisibility || {}),
          myWeb: false, // strictly hidden
        },
      },
      customTulips: {
        defaultColor: newSettings.tulipSettings?.allColor || 'golden',
        count: 12,
      },
      customWatering: {
        lastWateredAt: newSettings.gardenWatering?.lastWateredAt || new Date().toISOString(),
        wateredCount: newSettings.gardenWatering?.wateredCount || 10,
        wateringIntervalDays: newSettings.gardenWatering?.wateringIntervalDays || 3,
        canWaterLevel: 100,
        witheringEnabled: true,
      },
    };
    await onUpdateWeb(updated);
  };

  // Update memories list
  const handleSaveMemories = async (newMemories: MemoryItem[]) => {
    setMemories(newMemories);
    const updated: MyWebRequest = {
      ...request,
      customMemories: newMemories,
    };
    await onUpdateWeb(updated);
  };

  // Update letters list
  const handleSaveLetters = async (newLetters: DailyLetter[]) => {
    setLettersList(newLetters);
    const updated: MyWebRequest = {
      ...request,
      customLetters: newLetters,
    };
    await onUpdateWeb(updated);
  };

  // Handle single memory deletion
  const handleDeleteMemory = async (id: string) => {
    const updated = memories.filter((m) => m.id !== id);
    setMemories(updated);
    await onUpdateWeb({
      ...request,
      customMemories: updated,
    });
  };

  // Handle single letter deletion
  const handleDeleteLetter = async (id: string) => {
    const updated = lettersList.filter((l) => l.id !== id);
    setLettersList(updated);
    await onUpdateWeb({
      ...request,
      customLetters: updated,
    });
  };

  // Handle new memory from UploadModal
  const handleSaveNewMemory = async (
    newMemData: Omit<MemoryItem, 'id' | 'comments' | 'createdAt'>
  ) => {
    const newMemory: MemoryItem = {
      ...newMemData,
      id: `mem-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      comments: [],
      createdAt: new Date().toISOString(),
    };
    const updatedMemories = [newMemory, ...memories];
    setMemories(updatedMemories);
    await onUpdateWeb({
      ...request,
      customMemories: updatedMemories,
    });
    setIsUploadOpen(false);
  };

  return (
    <div className="min-h-screen w-full bg-[#f8fafc] text-slate-800 font-['Plus_Jakarta_Sans',sans-serif]">
      <AdminPage
        settings={effectiveSettings}
        memories={memories}
        dailyLetters={lettersList}
        specialEvents={lettersList}
        isAuthenticated={true}
        activeAdminProfile={activeProfile}
        currentSection={currentSection}
        onSectionChange={(sec) => setCurrentSection(sec)}
        onLogin={() => {}}
        onLogout={onLogoutPin}
        onNavigateToPublic={() => onClose()}
        onSaveSettings={handleSaveSettings}
        onUpdateMemories={handleSaveMemories}
        onUpdateDailyLetters={handleSaveLetters}
        onUpdateSpecialEvents={handleSaveLetters}
        onDeleteMemory={handleDeleteMemory}
        onDeleteDailyLetter={handleDeleteLetter}
        onOpenUploadModal={() => setIsUploadOpen(true)}
        isUserWeb={true}
        userWebOrderCode={request.orderCode}
      />

      {/* Global Upload Modal for custom web memories */}
      <UploadModal
        isOpen={isUploadOpen}
        settings={effectiveSettings}
        activeAdminProfile={activeProfile}
        onClose={() => setIsUploadOpen(false)}
        onSaveMemory={handleSaveNewMemory}
      />
    </div>
  );
};
